import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';

/**
 * Tooltip fijo en body, encima del disparador.
 * - Desktop: hover (mouseenter / mouseleave).
 * - Táctil / sin hover: tap en el disparador para abrir/cerrar; tap fuera para cerrar.
 */
@Directive({
  selector: '[Tooltip-Info]',
  standalone: true,
})
export class TooltipInfoDirective implements OnInit, OnDestroy {
  private static activeInstance: TooltipInfoDirective | undefined;

  @Input() tooltip = '';
  @Input() className = '';

  private myPopup: HTMLDivElement | undefined;
  private openedByClick = false;
  private positionRafId: number | undefined;

  isResponsive = false;

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  private get browserWindow(): Window | undefined {
    return globalThis.window ?? undefined;
  }

  ngOnInit(): void {
    this.isResponsive = !!this.browserWindow && this.browserWindow.innerWidth < 840;
  }

  ngOnDestroy(): void {
    this.removePopup();
  }

  /** En táctil no hay hover: hay que abrir con tap. */
  private get useTapToToggle(): boolean {
    if (!this.browserWindow?.matchMedia) {
      return false;
    }
    return (
      this.browserWindow.matchMedia('(hover: none)').matches ||
      this.browserWindow.matchMedia('(pointer: coarse)').matches
    );
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.openedByClick) {
      return;
    }
    if (!this.useTapToToggle) {
      this.createTooltipPopup();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.closeTransientPopup();
  }

  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    event.stopPropagation();
    this.openedByClick = true;
    TooltipInfoDirective.activeInstance = this;
    if (!this.myPopup) {
      this.createTooltipPopup();
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (!this.myPopup) {
      return;
    }

    const t = event.target as Node;

    // Dentro del tooltip o dentro del icono/disparador: no cerrar (para permitir seleccionar texto).
    if (this.myPopup.contains(t) || this.el.nativeElement.contains(t)) {
      return;
    }

    // Click fuera: cerrar.
    this.removePopup();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isResponsive = !!this.browserWindow && this.browserWindow.innerWidth < 840;
    if (this.myPopup) {
      this.positionPopup(this.myPopup);
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this.useTapToToggle && !this.myPopup) {
      this.createTooltipPopup();
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.closeTransientPopup();
  }

  private closeTransientPopup(): void {
    if (this.openedByClick) {
      return;
    }
    if (!this.useTapToToggle) {
      this.removePopup();
    }
  }

  @HostListener('keydown.escape', ['$event'])
  onHostEscape(event: Event): void {
    this.closeOnEscape(event);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscapeKeydown(event: Event): void {
    this.closeOnEscape(event);
  }

  @HostListener('document:keyup.escape', ['$event'])
  onDocumentEscapeKeyup(event: Event): void {
    this.closeOnEscape(event);
  }

  private closeOnEscape(event?: Event): void {
    if (this.myPopup) {
      event?.preventDefault();
      event?.stopPropagation();
      this.removePopup();
      this.el.nativeElement.blur();
    }
  }

  private removePopup(keepOpenedByClick = false): void {
    if (this.positionRafId !== undefined) {
      cancelAnimationFrame(this.positionRafId);
      this.positionRafId = undefined;
    }
    if (this.myPopup) {
      this.myPopup.remove();
      this.myPopup = undefined;
    }
    if (!keepOpenedByClick) {
      this.openedByClick = false;
    }
    if (TooltipInfoDirective.activeInstance === this) {
      TooltipInfoDirective.activeInstance = undefined;
    }
  }

  private getAnchorRect(): DOMRect {
    return this.el.nativeElement.getBoundingClientRect();
  }

  private positionPopup(popup: HTMLDivElement): void {
    const trigger = this.getAnchorRect();
    const anchorCenterX = trigger.left + trigger.width / 2;
    const gap = 8;
    const margin = 8;
    const arrowHeight = this.isResponsive ? 8 : 10;
    const h = popup.offsetHeight;
    const w = popup.offsetWidth;

    let top = trigger.top - h - gap - arrowHeight;
    let left = anchorCenterX;

    if (top < margin) {
      top = margin;
    }
    if (left - w / 2 < margin) {
      left = margin + w / 2;
    }
    const viewportWidth = this.browserWindow?.innerWidth ?? Number.POSITIVE_INFINITY;
    if (left + w / 2 > viewportWidth - margin) {
      left = viewportWidth - margin - w / 2;
    }

    popup.style.top = `${top}px`;
    popup.style.left = `${left}px`;
    popup.style.zIndex = '30000';

    const _layoutReflow = popup.offsetHeight;
  }

  private createTooltipPopup(): void {
    // Cerrar cualquier tooltip que esté abierto en otra instancia.
    if (TooltipInfoDirective.activeInstance && TooltipInfoDirective.activeInstance !== this) {
      TooltipInfoDirective.activeInstance.removePopup();
    }

    // Remueve el popup de esta misma instancia, pero conserva `openedByClick`
    // para que si el tooltip se abrió con click, siga "fijado".
    this.removePopup(true);

    const popup = document.createElement('div');
    popup.innerHTML = this.tooltip;
    popup.setAttribute(
      'class',
      this.isResponsive
        ? `tooltip-container-funds-responsive ${this.className}`.trim()
        : `tooltip-container-funds ${this.className}`.trim()
    );
    popup.setAttribute('role', 'status');
    popup.setAttribute('aria-live', 'polite');
    popup.setAttribute('aria-atomic', 'true');
    document.body.appendChild(popup);
    this.myPopup = popup;
    TooltipInfoDirective.activeInstance = this;

    const runPosition = () => {
      if (!this.myPopup || this.myPopup !== popup) {
        this.positionRafId = undefined;
        return;
      }
      this.positionPopup(popup);
      this.positionRafId = requestAnimationFrame(runPosition);
    };

    runPosition();
  }
}
